import { Children, Fragment, ReactElement, ReactNode, isValidElement } from 'react'

/**
 * Extracts child components by their displayName for compound component patterns.
 *
 * This enables flexible composition where the parent component controls the layout
 * and positioning of children, regardless of the order they're declared in JSX.
 * This inverts the control: the parent decides how children are rendered, not the children.
 *
 * ## Bucketing behavior — important
 *
 * - **Every** child with a `displayName` is bucketed under `blocks[displayName]`,
 *   regardless of whether `displayName` is declared in the generic `T`. The `T`
 *   parameter only types the return value — it does NOT act as a runtime filter.
 * - For keys NOT in `multipleKeys`, multiple children with the same displayName
 *   collide and **only the last one wins** (silent loss).
 * - The `content` key collects **only children without a `displayName`** (text
 *   nodes, plain `<div>`s, raw fragments). It is NOT a catch-all for "children
 *   not declared in `T`".
 *
 * ### When this matters
 *
 * If your component composes typed sub-components that **must preserve their
 * JSX order with respect to each other** (e.g. interleaved `<Item>` / `<Group>`
 * / `<Separator>` in a navigation menu), this helper is NOT the right tool —
 * you'll lose duplicates and you can't reconstruct the original order from the
 * separate buckets. Use a local `Children.toArray(children).filter(...)` for
 * that case, or list every kind in `multipleKeys` and concatenate (but the
 * order between different kinds is lost).
 *
 * @template T - A record type mapping displayNames to their component props types
 * @template K - An array of keys that should be treated as multiple (array)
 * @param children - React component children
 * @param multipleKeys - Optional array of keys (displayNames) that should be treated as multiple (array)
 * @returns A partial object where keys are displayNames and values are typed ReactElements (or arrays for multipleKeys),
 *          plus a `content` key containing only children without a `displayName`.
 *
 * @example
 * ```tsx
 * // Simple extraction (last one wins for duplicates):
 * const blocs = getTypedBlocksByTypes<HeroBlocksTypes>(children)
 *
 * // With multiple keys (always returns arrays for specified keys):
 * const blocks = getTypedBlocksByTypes<BlockTitleTypes, ['BlockTitleAction']>(
 *   children,
 *   ['BlockTitleAction']
 * )
 * // blocks.BlockTitleAction will always be an array (even if empty or single)
 * // blocks.BlockTitleCheckbox will be a single element (last one wins)
 * ```
 */

type ExtractedBlocks<T, K extends readonly (keyof T)[]> =
  // Multiple keys are always present as arrays (even if empty)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { [P in K[number]]: Array<ReactElement<any>> } &
    // Other keys are optional and return single elements
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Partial<{ [P in Exclude<keyof T, K[number]>]: ReactElement<any> }> &
    // Content is always optional
    { content?: ReactNode }

export function getTypedBlocksByTypes<
  T extends Record<string, unknown>,
  K extends readonly (keyof T)[] = [],
>(children: ReactNode, multipleKeys?: K): ExtractedBlocks<T, K> {
  const blocks: Record<string, ReactElement | ReactElement[] | ReactNode> = {}
  const unmatchedChildren: ReactNode[] = []

  // If children is a fragment element, extract its children first
  let processableChildren = children
  if (isValidElement(children) && children.type === Fragment) {
    processableChildren = (children.props as { children?: ReactNode }).children
  }

  const childrenArray = Children.toArray(processableChildren)

  childrenArray.forEach(child => {
    // Handle text nodes and non-element children
    if (!isValidElement(child)) {
      unmatchedChildren.push(child)
      return
    }

    // Process components with displayName (covers both plain functions and forwardRef/memo objects)
    if (child.type && (typeof child.type === 'function' || typeof child.type === 'object')) {
      const displayName = (child.type as { displayName?: string }).displayName as
        | keyof T
        | undefined
      if (displayName) {
        const isMultiple = multipleKeys?.includes(displayName)
        if (isMultiple) {
          // For multiple keys, always accumulate in array
          if (blocks[displayName as string]) {
            if (Array.isArray(blocks[displayName as string])) {
              ;(blocks[displayName as string] as ReactElement[]).push(child)
            } else {
              blocks[displayName as string] = [blocks[displayName as string] as ReactElement, child]
            }
          } else {
            blocks[displayName as string] = [child]
          }
        } else {
          // For single keys, always keep only the last one
          blocks[displayName as string] = child
        }
        return
      }
    }

    // If no displayName was found, it's unmatched content
    unmatchedChildren.push(child)
  })

  // Add content only if there are unmatched children
  if (unmatchedChildren.length > 0) {
    blocks.content = unmatchedChildren.length === 1 ? unmatchedChildren[0] : unmatchedChildren
  }

  // Ensure all multipleKeys are present as arrays (even if empty)
  if (multipleKeys) {
    for (const key of multipleKeys) {
      if (!Object.hasOwn(blocks, key)) {
        blocks[key as string] = []
      } else if (!Array.isArray(blocks[key as string])) {
        blocks[key as string] = [blocks[key as string] as ReactElement]
      }
    }
  }

  return blocks as ExtractedBlocks<T, K>
}
